import { usePostHog } from "@posthog/react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
	Copy,
	CreditCard,
	Crown,
	Loader2,
	Share2,
	XCircle,
	Zap,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { BrandButton } from "@/components/brand/brand-button";
import {
	BrandCard,
	BrandCardContent,
	BrandCardDescription,
	BrandCardHeader,
	BrandCardTitle,
} from "@/components/brand/brand-card";
import { CancelSubscriptionDialog } from "@/components/CancelSubscriptionDialog";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { DashboardPageHeader } from "@/components/dashboard/DashboardPageHeader";
import { LoadingScreen } from "@/components/LoadingScreen";
import { useProDialog } from "@/components/ProDialog";
import { useSession } from "@/hooks/auth-hooks";
import { useSubscription } from "@/hooks/use-subscription";
import { formatCredits } from "@/lib/billing-utils";
import { SUBSCRIPTION_DEFINITIONS } from "@/lib/plans";

export const Route = createFileRoute("/dashboard/billing")({
	component: BillingPage,
});

const pageShellClass =
	"mx-auto grid w-full max-w-none gap-8 px-4 py-8 sm:px-6 lg:grid-cols-[240px_minmax(0,1fr)] lg:px-8 lg:py-10 xl:max-w-[1800px]";
const sectionHeaderClass =
	"mb-5 flex flex-col gap-2 border-b border-border pb-5 sm:flex-row sm:items-start sm:justify-between";
const sectionIconClass =
	"flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-background text-muted-foreground";

function BillingPage() {
	const posthog = usePostHog();
	const { session, user, isPending } = useSession();
	const navigate = useNavigate();
	const { subscription, tier, refetch } = useSubscription(user?.id);
	const { openProDialog } = useProDialog();
	const [isLoadingBilling, setIsLoadingBilling] = useState(false);
	const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

	const { data: affiliate } = useQuery({
		enabled: !!session,
		queryFn: async () => {
			const response = await fetch("/api/affiliate/me", {
				credentials: "include",
			});
			if (!response.ok) throw new Error("Failed to load affiliate link");
			const data = (await response.json()) as {
				affiliate?: {
					affiliateCode: string;
					commissionMonths: number;
					commissionPercent: number;
					referralCount: number;
					referralUrl: string;
				};
			};
			return data.affiliate ?? null;
		},
		queryKey: ["affiliate", "me"],
	});

	const { data: billingStatus } = useQuery({
		enabled: !!session,
		queryFn: async () => {
			const response = await fetch("/api/workspaces?includeBilling=1", {
				credentials: "include",
			});
			if (!response.ok) return null;
			const data = (await response.json()) as {
				billingStatus?: {
					nextResetAt: string;
					remainingCreditUnits: number;
					usedCreditUnits: number;
					workspaceName: string | null;
				} | null;
			};
			return data.billingStatus ?? null;
		},
		queryKey: ["billing", "status", tier],
	});

	const handleManageBilling = async () => {
		setIsLoadingBilling(true);
		try {
			const result = await fetch("/api/stripe/portal", {
				method: "POST",
				credentials: "include",
			});
			const data = await result.json();
			if (data.url) {
				window.location.href = data.url;
			} else {
				toast.error("Failed to open billing portal");
				setIsLoadingBilling(false);
			}
		} catch {
			toast.error("Failed to open billing portal");
			setIsLoadingBilling(false);
		}
	};

	const handleUpgradePlan = () => {
		posthog?.capture("subscription_upgrade_clicked", { current_tier: tier });
		openProDialog();
	};

	const handleCopyAffiliateLink = async () => {
		if (!affiliate?.referralUrl) return;
		await navigator.clipboard.writeText(affiliate.referralUrl);
		toast.success("Affiliate link copied");
	};

	if (isPending) return <LoadingScreen />;

	if (!session) {
		navigate({ to: "/login" });
		return null;
	}

	return (
		<DashboardLayout>
			<div className={pageShellClass}>
				<div className="lg:sticky lg:top-24 lg:self-start">
					<DashboardPageHeader
						title="Billing"
						description="Review your plan, usage, billing date, and payment settings."
					/>
				</div>

				<BrandCard
					variant="default"
					padding="lg"
					className="bg-card shadow-none"
				>
					<BrandCardHeader className={sectionHeaderClass}>
						<div className="flex items-start gap-3">
							<div className={sectionIconClass}>
								<CreditCard className="size-4" />
							</div>
							<div>
								<BrandCardTitle className="text-base font-semibold tracking-normal">
									Subscription
								</BrandCardTitle>
								<BrandCardDescription className="text-sm leading-6">
									Manage your subscription and payment method
								</BrandCardDescription>
							</div>
						</div>
					</BrandCardHeader>
					<BrandCardContent>
						{subscription ? (
							<div className="space-y-4">
								<div className="rounded-md border border-border bg-background p-4">
									<div className="flex flex-wrap items-center gap-2">
										<Crown className="size-4 text-muted-foreground" />
										<span className="font-medium text-foreground">
											{SUBSCRIPTION_DEFINITIONS[tier].name}
										</span>
										<span className="rounded-sm border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
											{subscription.status === "active"
												? "Active"
												: subscription.status}
										</span>
									</div>
									{subscription.periodEnd && (
										<p className="mt-2 text-sm text-muted-foreground">
											{subscription.cancelAtPeriodEnd
												? `Ends on ${subscription.periodEnd.toLocaleDateString()}`
												: `Next billing date: ${subscription.periodEnd.toLocaleDateString()}`}
										</p>
									)}
								</div>
								{billingStatus && (
									<div className="rounded-md border border-border bg-background p-4">
										<div className="flex items-center justify-between gap-4">
											<p className="font-medium text-foreground">
												{billingStatus.workspaceName ?? "Active workspace"}
											</p>
											<span className="font-mono text-xs text-muted-foreground">
												{formatCredits(billingStatus.remainingCreditUnits)} left
											</span>
										</div>
										<div className="mt-4 grid gap-3 text-sm text-muted-foreground sm:grid-cols-3">
											<div>
												<p className="text-xs">Used this cycle</p>
												<p className="mt-1 font-medium text-foreground">
													{formatCredits(billingStatus.usedCreditUnits)}
												</p>
											</div>
											<div>
												<p className="text-xs">Remaining</p>
												<p className="mt-1 font-medium text-foreground">
													{formatCredits(billingStatus.remainingCreditUnits)}
												</p>
											</div>
											<div>
												<p className="text-xs">Resets</p>
												<p className="mt-1 font-medium text-foreground">
													{new Date(
														billingStatus.nextResetAt,
													).toLocaleDateString()}
												</p>
											</div>
										</div>
									</div>
								)}
								<div className="flex flex-wrap gap-2">
									<BrandButton
										size="sm"
										onClick={handleManageBilling}
										disabled={isLoadingBilling}
									>
										{isLoadingBilling ? (
											<>
												<Loader2 className="size-4 animate-spin" />
												Loading…
											</>
										) : (
											<>
												<CreditCard className="size-4" />
												Manage payment method
											</>
										)}
									</BrandButton>
									{tier !== "max" && (
										<BrandButton
											variant="outline"
											size="sm"
											onClick={handleUpgradePlan}
											className="h-9 text-sm"
										>
											<Zap className="size-4" />
											Upgrade plan
										</BrandButton>
									)}
									{subscription.status === "active" &&
										!subscription.cancelAtPeriodEnd && (
											<BrandButton
												variant="ghost"
												size="sm"
												onClick={() => setIsCancelDialogOpen(true)}
												className="h-9 text-sm text-destructive hover:bg-destructive/10 hover:text-destructive"
											>
												<XCircle className="size-4" />
												Cancel subscription
											</BrandButton>
										)}
								</div>
							</div>
						) : (
							<div className="space-y-4">
								<p className="max-w-xl text-sm leading-6 text-muted-foreground">
									You do not have an active posting plan. Start Pro to unlock
									connected accounts, scheduled posts, team seats, and workspace
									capacity.
								</p>
								<BrandButton size="sm" onClick={openProDialog}>
									<Crown className="size-4" />
									Upgrade
								</BrandButton>
							</div>
						)}
					</BrandCardContent>
				</BrandCard>
				<BrandCard
					variant="default"
					padding="lg"
					className="bg-card shadow-none"
				>
					<BrandCardHeader className={sectionHeaderClass}>
						<div className="flex items-start gap-3">
							<div className={sectionIconClass}>
								<Share2 className="size-4" />
							</div>
							<div>
								<BrandCardTitle className="text-base font-semibold tracking-normal">
									Affiliate link
								</BrandCardTitle>
								<BrandCardDescription className="text-sm leading-6">
									Earn 30% of referred subscription revenue for 12 months.
								</BrandCardDescription>
							</div>
						</div>
					</BrandCardHeader>
					<BrandCardContent>
						<div className="space-y-4">
							<div className="rounded-md border border-border bg-background p-4">
								<p className="text-xs text-muted-foreground">Your link</p>
								<p className="mt-2 break-all font-mono text-sm text-foreground">
									{affiliate?.referralUrl ?? "Loading…"}
								</p>
							</div>
							<div className="grid gap-3 text-sm sm:grid-cols-3">
								<div className="rounded-md border border-border bg-background p-4">
									<p className="text-xs text-muted-foreground">Commission</p>
									<p className="mt-1 font-medium text-foreground">
										{affiliate?.commissionPercent ?? 30}%
									</p>
								</div>
								<div className="rounded-md border border-border bg-background p-4">
									<p className="text-xs text-muted-foreground">Term</p>
									<p className="mt-1 font-medium text-foreground">
										{affiliate?.commissionMonths ?? 12} months
									</p>
								</div>
								<div className="rounded-md border border-border bg-background p-4">
									<p className="text-xs text-muted-foreground">Referrals</p>
									<p className="mt-1 font-medium text-foreground">
										{affiliate?.referralCount ?? 0}
									</p>
								</div>
							</div>
							<BrandButton
								size="sm"
								onClick={handleCopyAffiliateLink}
								disabled={!affiliate?.referralUrl}
							>
								<Copy className="size-4" />
								Copy link
							</BrandButton>
						</div>
					</BrandCardContent>
				</BrandCard>
			</div>
			{subscription && (
				<CancelSubscriptionDialog
					open={isCancelDialogOpen}
					onOpenChange={setIsCancelDialogOpen}
					subscription={subscription}
					tier={tier}
					onCancelled={refetch}
				/>
			)}
		</DashboardLayout>
	);
}
